import React, { useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import * as d3 from "d3";

interface SpeedometerChartProps {
    value: number; // Should be between 0 and 1
}

export const SpeedometerChart: React.FC<SpeedometerChartProps> = ({ value }) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(400);
    const [height, setHeight] = useState(width / 2 + 50);

    // Clamp value between 0 and 1
    const clampedValue = Math.max(0, Math.min(1, value));

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(Math.min(entry.contentRect.width, 400));
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);

        // Calculate dimensions
        const height = width / 2 + 50; // Half circle plus some space for text
        const radius = width / 2.2;
        const centerX = width / 2;
        const centerY = height - 50;

        // Set up SVG
        svg.attr("viewBox", `0 0 ${width} ${height}`);

        // Clear existing content
        svg.selectAll("*").remove();

        // Create color scale from transparent to primary main
        const colorScale = d3.scaleLinear<string>()
            .domain([0, 1])
            .range(["rgba(0,0,0,0)", theme.palette.primary.main]);

        // Create gradient definition
        const defs = svg.append("defs");
        const gradient = defs.append("linearGradient")
            .attr("id", "speedometer-gradient")
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "100%")
            .attr("y2", "0%");

        // Add gradient stops
        const numStops = 20;
        // Replace the gradient stops loop
        for (let i = 0; i <= numStops; i++) {
            const offset = i / numStops;
            const color = colorScale(offset);
            gradient.append("stop")
                .attr("offset", `${offset * 100}%`)
                .attr("stop-color", color)
                .attr("stop-opacity", offset); // Fade in opacity
        }

        // Create arc generator for the background
        const arc = d3.arc()
            .innerRadius(radius * 0.6)
            .outerRadius(radius)
            .startAngle(-Math.PI / 2)
            .endAngle(Math.PI / 2);

        // Create main group
        const mainGroup = svg.append("g")
            .attr("transform", `translate(${centerX}, ${centerY})`);

        // Draw border arc (same radii as color arc)
        const borderArc = d3.arc()
            .innerRadius(radius * 0.6)
            .outerRadius(radius)
            .startAngle(-Math.PI / 2)
            .endAngle(Math.PI / 2);

        mainGroup.append("path")
            .attr("d", borderArc as any)
            .attr("fill", "none")
            .attr("stroke", theme.palette.text.primary)
            .attr("stroke-width", 1.5);

        // Draw background arc
        mainGroup.append("path")
            .attr("d", arc as any)
            .attr("fill", "url(#speedometer-gradient)");

        // Calculate needle angle based on value (-90° to +90°)
        const needleAngle = -Math.PI + (clampedValue * Math.PI);

        // Draw needle
        const needleLength = radius * 0.8;
        const needleWidth = 3;

        const needleGroup = mainGroup.append("g")
            .attr("class", "needle");

        // Needle line
        needleGroup.append("line")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", Math.cos(needleAngle) * needleLength)
            .attr("y2", Math.sin(needleAngle) * needleLength)
            .attr("stroke", theme.palette.text.primary)
            .attr("stroke-width", needleWidth)
            .attr("stroke-linecap", "round");

        // Needle center circle
        needleGroup.append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", 8)
            .attr("fill", theme.palette.background.paper)
            .attr("stroke", theme.palette.text.primary)
            .attr("stroke-width", 2);

        // Add value text in center
        mainGroup.append("text")
            .attr("x", 0)
            .attr("y", 40)
            .attr("text-anchor", "middle")
            .attr("fill", theme.palette.text.primary)
            .style("font-size", "18px")
            .text(`${(clampedValue * 100).toFixed(0)}`);

    }, [clampedValue, width, theme]);

    return (
        <Box ref={containerRef} sx={{ width: "100%", position: "relative" }}>
            <svg
                ref={svgRef}
                width={width}
                height={height}
                style={{ background: "transparent" }}
            />
        </Box>
    );
};
